import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const ProfileIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M5 21a7 7 0 0 1 14 0" />
  </svg>
);

const HomeIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
  </svg>
);

const PurchasedIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 6h15l-1.5 9h-12z" />
    <path d="M6 6L5 3H2" />
    <circle cx="9" cy="20" r="1" />
    <circle cx="18" cy="20" r="1" />
  </svg>
);

const HistoryIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
    <path d="M12 7v6l4 2" />
  </svg>
);

const ServicesIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v20M2 12h20" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SignOutIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

const BurgerIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
  </svg>
);

const menuItems = [
  { name: 'PROFILE',    href: '/profile',    icon: <ProfileIcon /> },
  { name: 'HOME',       href: '/',           icon: <HomeIcon /> },
  { name: 'PURCHASED',  href: '/purchased',  icon: <PurchasedIcon /> },
  { name: 'TREATMENTS', href: '/treatments', icon: <ServicesIcon /> },
  { name: 'HISTORY',    href: '/history',    icon: <HistoryIcon /> },
  { name: 'SETTINGS',   href: '/settings',   icon: <SettingsIcon /> },
];

const ClientSidebar = ({
  isOpen,
  onOpenChange,
  onLogout,
  clientName,
  clientEmail,
  clientAvatar,
  showTrigger = true,
}) => {
  const location = useLocation();
  const activeHref = location.pathname;
  const isHome = activeHref === '/';

  const emailLabel = (clientEmail || '').trim();
  const emailProfileName = emailLabel ? emailLabel.split('@')[0] : '';
  const displayName = (clientName || '').trim() || emailProfileName || 'Client';
  const emailInitial = emailLabel ? emailLabel.charAt(0).toUpperCase() : 'C';

  const closeTimerRef = useRef(null);
  const menuRef = useRef(null);

  // Detect mobile (≤768px) to switch between hover dropdown and full side drawer
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock body scroll on mobile when drawer open
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, isMobile]);

  const handleMenuEnter = () => {
    if (isMobile) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    onOpenChange(true);
  };

  const handleMenuLeave = () => {
    if (isMobile) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onOpenChange(false);
      closeTimerRef.current = null;
    }, 2000);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      onOpenChange(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    window.location.href = '/';
  };

  const close = () => onOpenChange(false);

  // ── Shared menu content ──────────────────────────────────────
  const menuContent = (
    <>
      {/* User info card */}
      <div className="relative mb-4 flex items-center gap-3 rounded-xl border border-[#f3e9e0] bg-white/70 px-3 py-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#eaded5] bg-white text-[#6f4a42]">
          {clientAvatar ? (
            <img src={clientAvatar} alt={displayName} className="h-8 w-8 rounded-md object-cover" />
          ) : (
            <span className="text-sm font-bold">{emailInitial}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-[10px] font-semibold uppercase tracking-[0.3em] text-[#b07b60]">Hygea</p>
          <p className="truncate text-sm font-semibold text-[#2f2622]">{displayName}</p>
          <p className="truncate text-[11px] text-[#8a7a71]">{emailLabel || 'No email'}</p>
        </div>
      </div>

      {/* Section label */}
      <div className="px-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-[#b07b60]">
        Navigation
      </div>

      {/* Nav links */}
      <nav className="mt-3 space-y-1">
        {menuItems.map((item) => {
          const isActive = activeHref === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={close}
              className={`group relative flex items-center rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-[#f7efe9] text-[#2f2622]'
                  : 'text-[#6f4a42] hover:bg-[#fbf3ee]'
              }`}
            >
              <span className={`flex w-6 justify-center ${isActive ? 'text-[#7a4e46]' : 'text-[#8b6f63]'}`}>
                {item.icon}
              </span>
              <span className="ml-3 flex-1 text-left tracking-wide">{item.name}</span>
              {isActive && (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#b07b60]" />
              )}
            </Link>
          );
        })}
      </nav>

      <div className="mt-4 h-px w-full bg-[#efe4db]" />

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        className="mt-4 flex w-full items-center rounded-lg border border-[#eaded5] px-3 py-2.5 text-sm font-semibold text-[#6f4a42] transition-all duration-200 hover:bg-[#fbf3ee]"
      >
        <span className="flex w-6 justify-center text-[#8b6f63]">
          <SignOutIcon />
        </span>
        <span className="ml-3 flex-1 text-left tracking-wide">Log out</span>
      </button>
    </>
  );

  // ── Mobile: full-height left side drawer ─────────────────────
  if (isMobile) {
    return (
      <>
        {/* Trigger button — top-left */}
        {showTrigger && (
          <div className={`fixed left-4 z-40 ${isHome ? 'top-14' : 'top-4'}`}>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => onOpenChange(true)}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#eaded5] bg-white/85 text-[#6f4a42] shadow-[0_10px_25px_rgba(33,24,18,0.18)] backdrop-blur-md"
            >
              <BurgerIcon />
            </button>
          </div>
        )}

        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-[60] transition-all duration-300 ${
            isOpen
              ? 'bg-black/40 backdrop-blur-sm pointer-events-auto'
              : 'bg-transparent pointer-events-none'
          }`}
          onClick={close}
        />

        {/* Drawer panel — slides in from LEFT */}
        <div
          className={`fixed top-0 left-0 z-[70] h-full w-72 transition-transform duration-500 ease-in-out ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            background: 'linear-gradient(160deg, #f9f3ee 0%, #f0e4d7 50%, #e8d5c4 100%)',
          }}
        >
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#b37b65] via-[#c9956f] to-[#d4a574]" />

          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 pt-6 pb-4 border-b border-[#d9c4b0]/50">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#b07b60]">Menu</p>
            <button
              onClick={close}
              className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/60 text-[#6f4a42] hover:bg-white transition-all border border-[#eaded5]"
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto h-[calc(100%-64px)] px-4 py-4">
            {menuContent}
          </div>

          {/* Bottom branding */}
          <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-1 opacity-25 pointer-events-none">
            <div className="w-8 h-px bg-[#b37b65]" />
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#6f4a42] font-bold">Hygea Wellness Spa</p>
            <div className="w-8 h-px bg-[#b37b65]" />
          </div>
        </div>
      </>
    );
  }

  // ── Desktop: original hover dropdown ─────────────────────────
  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/0 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <div className={`fixed left-4 z-40 ${isHome ? 'top-14' : 'top-4'}`} ref={menuRef}>
        <div onMouseEnter={handleMenuEnter} onMouseLeave={handleMenuLeave} className="relative">
          {showTrigger && (
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => onOpenChange(!isOpen)}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#eaded5] bg-white/85 text-[#6f4a42] shadow-[0_10px_25px_rgba(33,24,18,0.18)] backdrop-blur-md"
            >
              <BurgerIcon />
            </button>
          )}

          <div
            className={`absolute left-0 mt-3 w-72 origin-top-left rounded-2xl border border-[#efe4db] bg-white/78 p-4 shadow-[0_24px_55px_rgba(33,24,18,0.16)] backdrop-blur-xl transition-all duration-300 ${
              isOpen
                ? 'opacity-100 translate-y-0 scale-100'
                : 'pointer-events-none opacity-0 -translate-y-2 scale-95'
            }`}
          >
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[linear-gradient(135deg,rgba(255,255,255,0.85),rgba(255,255,255,0.55))]" />
            <div className="relative">
              {menuContent}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientSidebar;